import dayjs from 'dayjs'
import { createContext, useContext } from 'react'

const DateFilterContext = createContext<{
  toNextWeek: () => void
  toPreviousWeek: () => void
  toToday: () => void
  isToday: () => boolean
  dateFilterValue: number[] | null
  canGoToPreviousWeek: () => boolean
  canGoToNextWeek: () => boolean
}>({
  toNextWeek: () => {},
  toPreviousWeek: () => {},
  toToday: () => {},
  isToday: () => false,
  dateFilterValue: null,
  canGoToPreviousWeek: () => true,
  canGoToNextWeek: () => true
})


export function useDateFilterContext() {
  return useContext(DateFilterContext)
}


export function DateFilterProvider({ children, state }: { children: React.ReactNode, state: [number[] | null, React.Dispatch<React.SetStateAction<number[] | null>>] }) {
  const [dateFilterTimeframe, setDateFilterTimeframe] = state

  const WEEKS_BACK_LIMIT = 3
  const WEEKS_FORWARD_LIMIT = 5

  const toNextWeek = () => {
    if (dateFilterTimeframe && canGoToNextWeek()) {
        setDateFilterTimeframe([
        dayjs(dateFilterTimeframe[0]).add(1, 'week').startOf('isoWeek').valueOf(),
        dayjs(dateFilterTimeframe[1]).add(1, 'week').endOf('isoWeek').valueOf()
     ])
    }
  }

  const toPreviousWeek = () => {
    if (dateFilterTimeframe && canGoToPreviousWeek()) {
        setDateFilterTimeframe([
        dayjs(dateFilterTimeframe[0]).subtract(1, 'week').startOf('isoWeek').valueOf(),
        dayjs(dateFilterTimeframe[1]).subtract(1, 'week').endOf('isoWeek').valueOf()
        ])
    }
  }

  const toToday = () => {
    setDateFilterTimeframe([
      dayjs().startOf('isoWeek').valueOf(),
      dayjs().endOf('isoWeek').valueOf()
    ])
  }

  const isToday = () => {
    if (!dateFilterTimeframe) return false
    const today = dayjs()
    return today.isAfter(dayjs(dateFilterTimeframe[0])) && today.isBefore(dayjs(dateFilterTimeframe[1]))
  }

  const canGoToPreviousWeek = () => {
    if (!dateFilterTimeframe) return false
    const currentWeekStart = dayjs(dateFilterTimeframe[0])
    const earliestAllowedWeek = dayjs().subtract(WEEKS_BACK_LIMIT, 'week').startOf('isoWeek')
    return currentWeekStart.isAfter(earliestAllowedWeek)
  }

  const canGoToNextWeek = () => {
    if (!dateFilterTimeframe) return false
    const currentWeekStart = dayjs(dateFilterTimeframe[0])
    const latestAllowedWeek = dayjs().add(WEEKS_FORWARD_LIMIT, 'week').startOf('isoWeek')
    return currentWeekStart.isBefore(latestAllowedWeek)
  }

  return (
    <DateFilterContext.Provider value={{ 
      toNextWeek, 
      toPreviousWeek, 
      toToday, 
      isToday, 
      dateFilterValue: dateFilterTimeframe,
      canGoToPreviousWeek,
      canGoToNextWeek
    }}>
      {children}
    </DateFilterContext.Provider>
  )
}